import { Injector, Service, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';
import { PageTitleService } from '@core/services/page-title.service/page-title.service';

/**
 * Treats route `title` values as translation keys and marks the environment,
 * the way the legacy `PageService` did: "Klienci | test" everywhere except
 * production, where the title stands alone.
 *
 * It also feeds the in-page heading (`PageTitleService` → `app-page-title`),
 * again as legacy's `PageService` did — the tab title and the heading come from
 * the same route key, so resolving it twice would only invite drift. A route
 * opts out of the heading with `data: { hideTitle: true }`; legacy carried the
 * same flag on the same screens.
 */
@Service()
export class TranslatedTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly pageTitle = inject(PageTitleService);
  // Lazy on purpose: this strategy is constructed together with the Router,
  // and instantiating TranslateService that early creates a circular
  // dependency (its loader issues HTTP requests through the interceptors).
  private readonly injector = inject(Injector);

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const key = this.buildTitle(snapshot);

    this.pageTitle.setTitleKey(key && !this.isHeadingHidden(snapshot) ? key : null);

    const page = key ? this.injector.get(TranslateService).instant(key) : null;
    if (!page) {
      this.title.setTitle(environment.appName);
      return;
    }
    this.title.setTitle(
      environment.production ? page : `${page} | ${environment.envName}`,
    );
  }

  /** The flag lives on the deepest activated route, as in legacy's `PageService`. */
  private isHeadingHidden(snapshot: RouterStateSnapshot): boolean {
    let route: ActivatedRouteSnapshot = snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.data['hideTitle'] === true;
  }
}
